import { z } from 'zod/v4';
import {
  DEFAULT_PAGE_SIZE,
  FILTER_MAX_LENGTH,
  MAX_PAGE_SIZE,
  RetracedFlags,
} from '../constants.js';
import { QuestionnaireBuilderStatisticsSchema } from './questionnaire-builder.js';
import { QuestionnaireStatuses } from './questionnaire-create.js';

/**
 * Sort columns the questionnaires list accepts — mirrors `SORT_COLUMNS_ENUM`
 * in the legacy `routes/schemas/v1/request/questionnaire.js`. Each key maps to
 * a SQL expression in `core/db/questionnaires/filters.ts` (`SORT_COLUMNS`);
 * keep the two in sync when adding or removing a column.
 */
export const QuestionnairesListSortColumns = [
  'name',
  'status',
  'dueDate',
  'firstRequestDate',
  'createdAt',
  'updatedAt',
  'invitedCompanies',
  'questionCount',
  'timesUsedAsTemplate',
  'isActive',
] as const;

export type QuestionnairesListSortColumn =
  (typeof QuestionnairesListSortColumns)[number];

/**
 * `column:DIRECTION` pairs, comma-separated. The legacy JSON-Schema pattern
 * allowed multi-column sort (`name:ASC,status:DESC`), so the port does too.
 * Lowercase directions are accepted as a superset — the builder upper-cases.
 */
const sortParamSchema = z
  .string()
  .regex(/^[a-zA-Z]+:(ASC|DESC|asc|desc)(,[a-zA-Z]+:(ASC|DESC|asc|desc))*$/)
  .optional();

/**
 * Fastify coerces `?flag=true` into a real boolean, but callers building the
 * query string with `URLSearchParams` send the literal string. Accept both,
 * matching how the legacy JSON-Schema `boolean` behaved in practice.
 */
const booleanLike = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform(v => v === true || v === 'true');

export const QuestionnairesListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  sort: sortParamSchema,
  filter: z.string().max(FILTER_MAX_LENGTH).optional(),
  isTemplate: booleanLike,
  isArchived: booleanLike,
  myTemplates: booleanLike,
});

export type QuestionnairesListQuery = z.infer<
  typeof QuestionnairesListQuerySchema
>;

/**
 * Row shape returned by the questionnaires list. `looseObject` because the
 * endpoint serves two different column sets — the questionnaire list adds
 * `statistics` / `invitedCompanies` / `firstRequestDate` / `isArchived`, the
 * templates list adds `isActive` / `timesUsedAsTemplate` — and the audit
 * columns come along on both. Every surface-specific column is therefore
 * `.optional()`: absent on the other surface is normal, not an error.
 *
 * **This schema is the frontends' type for these rows** — it is imported by
 * `frontend-v2/src/api/questionnaires/types.ts` rather than re-declared there.
 * A field the FE reads must be declared here or it infers as `unknown`. The
 * prose list this docblock used to carry ("field reads in the FE — must stay
 * populated") was that contract written down where no compiler could check it;
 * the declarations below replace it.
 *
 * Timestamps are `z.string()` because `transformQuestionnaireRow` ISO-normalises
 * them before they reach the serialiser — see `toIso` there for why that cannot
 * be expressed in the schema alone. Columns no frontend reads (`isArchived`,
 * `isActive`, the fifteen audit ids) stay undeclared and ride along on the
 * `looseObject`: declaring a field makes the serialiser *validate* it, so
 * declaring one nothing reads only buys a way to 500.
 */
export const QuestionnaireListRowSchema = z.looseObject({
  id: z.string(),
  // ─── NOT NULL, and closed value domains ────────────────────────────────────
  // These columns carry NOT NULL constraints and are selected by every query
  // feeding this schema, so they are required rather than
  // `.nullable().optional()` — which was looser than the database and looser
  // than what the frontends assumed.
  //
  // `status` and `isRetraced` are enums, not bare strings. Neither column has a
  // CHECK constraint, but on this platform that is the norm: value domains are
  // enforced in the application layer on both sides. This module already closes
  // them on the way *in* — `questionnaire-create.ts` and `questionnaire-update.ts`
  // both validate `z.enum(QuestionnaireStatuses)` — so closing them on the way
  // out makes one definition serve the write path, the read path and both
  // frontends. Staging agrees: `ACTIVE` (900) / `DRAFT` (740), `NO` / `YES`.
  companyId: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  status: z.enum(QuestionnaireStatuses),
  isRetraced: z.enum(RetracedFlags),
  isTemplate: z.number(),
  templateId: z.string().nullable().optional(),
  // `COUNT(...)` columns. Postgres types these `bigint`, which node-postgres
  // returns as a *string* by default — but the adapter registers a global
  // parser for OID 20 (`backend/src/lib/db/adapters/postgres.ts:166`), so they
  // arrive as numbers. `get.list.integration.test.ts` pins that
  // (`expect(body.data[0].questionCount).toBe(2)`). Declared `z.number()` on
  // that evidence: the `z.string()` half `questionCount` used to allow was
  // vestigial, and carrying it through to the frontends would infer
  // `number | string` onto every count cell.
  questionCount: z.number().nullable().optional(),
  invitedCompanies: z.number().nullable().optional(),
  timesUsedAsTemplate: z.number().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  firstRequestDate: z.string().nullable().optional(),
  updatedByUserName: z.string().nullable().optional(),
  updatedByCompanyName: z.string().nullable().optional(),
  // The same `supplier_questionnaires.statistics` blob that
  // `QuestionnaireBuilderDetailSchema` already validates with this schema on a
  // live route, so it carries no new rejection risk here. Imported rather than
  // re-declared for the same reason this file exists at all — there were three
  // copies of this shape in frontend-v2 alone.
  statistics: QuestionnaireBuilderStatisticsSchema.nullable().optional(),
});

export type QuestionnaireListRow = z.infer<typeof QuestionnaireListRowSchema>;

/**
 * The rows of the questionnaires list.
 *
 * Just the array — the `{metadata, data}` envelope around it comes from
 * `buildResponseSchemas({ 200: { type: 'data', schema, paginated: true } })` on the
 * route, the same builder every other paginated route on the platform uses. Nothing
 * here describes the envelope, so there is one definition of it rather than six.
 */
export const QuestionnairesListResponseSchema = z.array(
  QuestionnaireListRowSchema,
);

export type QuestionnairesListResponse = z.infer<
  typeof QuestionnairesListResponseSchema
>;
